import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Stethoscope, Star, Phone, Video, MapPin, Filter, ChevronRight, Sparkles } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { listDoctors, careFilters } from "../lib/api";
import { toast } from "sonner";

function Chip({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`whitespace-nowrap rounded-full px-3 py-1.5 text-[11px] font-extrabold uppercase tracking-wider transition-colors ${active ? "bg-primary text-primary-foreground" : "bg-card border border-border text-foreground/70 hover:border-primary/40 hover:text-foreground"}`}
    >
      {children}
    </button>
  );
}

export default function CarePage() {
  const [doctors, setDoctors] = useState([]);
  const [filters, setFilters] = useState({ cities: [], tags: [], modes: [] });
  const [city, setCity] = useState("");
  const [tag, setTag] = useState("");
  const [mode, setMode] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    careFilters().then(setFilters).catch(() => {});
  }, []);
  useEffect(() => {
    setLoading(true);
    listDoctors({ city, tag, mode }).then(setDoctors).catch(() => {}).finally(() => setLoading(false));
  }, [city, tag, mode]);

  return (
    <div className="kanya-page-pad-bottom" data-testid="care-page">
      <PageHeader eyebrow="Care" title="Find your specialist" subtitle="Curated, kind clinicians for cycle, hormones, mind and pregnancy." />

      <div className="px-4 space-y-3" data-testid="care-filters">
        <div className="flex gap-2 overflow-x-auto no-scrollbar">
          <Chip active={!city} onClick={() => setCity("")} testid="care-city-all">All cities</Chip>
          {filters.cities.map((c) => (
            <Chip key={c} active={city === c} onClick={() => setCity(c)} testid={`care-city-${c.toLowerCase()}`}>{c}</Chip>
          ))}
        </div>
        <div className="flex gap-2 overflow-x-auto no-scrollbar">
          <Chip active={!tag} onClick={() => setTag("")} testid="care-tag-all">All needs</Chip>
          {filters.tags.map((t) => (
            <Chip key={t} active={tag === t} onClick={() => setTag(t)} testid={`care-tag-${t.toLowerCase().replace(/\s+/g,'-')}`}>{t}</Chip>
          ))}
        </div>
        <div className="flex gap-2">
          <Chip active={!mode} onClick={() => setMode("")} testid="care-mode-any">Any visit</Chip>
          <Chip active={mode === "clinic"} onClick={() => setMode("clinic")} testid="care-mode-clinic">In-clinic</Chip>
          <Chip active={mode === "telehealth"} onClick={() => setMode("telehealth")} testid="care-mode-telehealth">Telehealth</Chip>
        </div>
      </div>

      <div className="px-4 mt-4 space-y-2.5" data-testid="care-doctor-list">
        {loading && <div className="shimmer h-24 rounded-2xl" />}
        {!loading && doctors.length === 0 && (
          <div className="rounded-2xl border border-border bg-muted/30 p-6 text-center">
            <Stethoscope size={20} className="mx-auto text-muted-foreground" />
            <p className="text-[13px] text-muted-foreground mt-2 font-medium">No clinicians match these filters yet.</p>
          </div>
        )}
        {!loading && doctors.map((d, i) => (
          <motion.div
            key={d.id}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.03 }}
            className="rounded-[20px] bg-card border border-border p-4 hover:border-primary/40 transition-colors"
            data-testid={`doctor-card-${d.id}`}
          >
            <div className="flex items-start gap-3">
              <div className="h-11 w-11 rounded-2xl bg-blush text-primary flex items-center justify-center shrink-0">
                <Stethoscope size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[10px] uppercase tracking-wider font-extrabold text-primary truncate">{d.specialty}</p>
                  <span className="inline-flex items-center gap-1 text-[10px] font-extrabold tracking-wider text-foreground/70">
                    <Star size={10} className="text-primary fill-primary" /> {d.rating?.toFixed?.(1) ?? d.rating}
                  </span>
                </div>
                <p className="font-extrabold text-[15px] tracking-tight">{d.name}</p>
                <p className="text-[11.5px] text-muted-foreground font-medium flex items-center gap-1 mt-0.5">
                  <MapPin size={11} /> {d.area}, {d.city} · {d.experience_years}y
                </p>
                <p className="text-[12.5px] text-foreground/80 font-medium mt-1.5 leading-relaxed line-clamp-2">{d.blurb}</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {d.tags?.slice(0, 3).map((t) => (
                    <span key={t} className="chip chip-blush" style={{ fontSize: 9 }}>{t}</span>
                  ))}
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <a
                    href={`tel:${d.phone}`}
                    onClick={() => toast.success("Opening dialer…")}
                    className="inline-flex items-center gap-1.5 text-[12px] font-bold rounded-full bg-primary text-primary-foreground px-3 py-1.5"
                    data-testid={`doctor-call-${d.id}`}
                  >
                    <Phone size={12} /> Call
                  </a>
                  {d.modes?.includes("telehealth") && (
                    <span className="inline-flex items-center gap-1.5 text-[12px] font-bold rounded-full bg-aquamarine-soft text-foreground px-3 py-1.5">
                      <Video size={12} /> Telehealth
                    </span>
                  )}
                  <span className="ml-auto text-[11px] font-bold text-muted-foreground">₹{d.consult_fee_inr}</span>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
        <div className="rounded-2xl bg-blush p-4 flex items-start gap-3 mt-4">
          <Sparkles size={14} className="text-primary mt-0.5 shrink-0" />
          <p className="text-[12px] text-foreground/85 leading-relaxed font-medium">
            Knya does not run, employ, or earn commission from any clinic listed. This directory is curated for trust and clarity — always verify credentials.
          </p>
        </div>
      </div>
    </div>
  );
}
